import { useMemo } from 'react';

import { useFeatureFlag } from '@xemplo/feature-flag';
import { GpFeature, PAYRUN_TABLE_FILTER_FORM_ID } from '@xemplo/gp-constants';
import {
  PayrollProcessingSystemId,
  PAYRUN_STATUS_IDS,
  PayrunsTableType,
  PayrunSteps,
  PayrunTableFilterQuery,
} from '@xemplo/gp-types';
import { getStepStatusIds } from '@xemplo/gp-utils';
import { usePayrunsType } from '@xemplo/use-payruns-type';
import { useFormValues } from '@xemplo/xemplo-form';

import {
  allActiveStepIds,
  expandMilestonesToStatusIds,
} from './payrun-base-table.helper';

export const useCombinedPayrunFilters = () => {
  const formData = useFormValues<PayrunTableFilterQuery>(PAYRUN_TABLE_FILTER_FORM_ID);

  const { payrunsType } = usePayrunsType();
  const { isEnabled } = useFeatureFlag();

  const ppsids = useMemo(
    () =>
      isEnabled(GpFeature.XemploPay)
        ? [PayrollProcessingSystemId.KeyPay, PayrollProcessingSystemId.XemploPay]
        : [PayrollProcessingSystemId.KeyPay],
    [isEnabled],
  );

  const defaultStatusIds = useMemo(() => {
    switch (payrunsType) {
      case PayrunsTableType.SystemGenerated:
        return [
          PAYRUN_STATUS_IDS.DRAFT_SYSTEM_TRIGGERED,
          PAYRUN_STATUS_IDS.DRAFT_USER_TRIGGERED,
        ];
      case PayrunsTableType.Complete:
        return getStepStatusIds(PayrunSteps.Complete);
      default: // PayrunsTableType.Active
        return allActiveStepIds;
    }
  }, [payrunsType]);

  // statusIds come from either the tab default or the selected milestones. Selected
  // milestones (steps) are expanded to their constituent status ids and replace the default.
  return useMemo(() => {
    const { milestones, ...rest } = formData ?? {};
    return {
      ...rest,
      statusIds: expandMilestonesToStatusIds(milestones, defaultStatusIds),
      ppsids,
    };
  }, [defaultStatusIds, formData, ppsids]);
};
